export interface IAlineaHistoryRecord {
  game_id?: string;
  metric?: string;
  metric_name?: string;
  platform?: string | string[];
  region?: string;
  release_date?: string;
  date?: string;
  latest_time?: string;
  metric_value?: string | number | null;
  value?: string | number | null;
  source?: string;
  source_quality?: string;
  [key: string]: unknown;
}

export interface IAlineaTrueDataMetricMapping {
  trueDataMetric: TAlineaTrueDataMetric;
  /** History 记录中允许作为该页面指标来源的平台值。 */
  allowedPlatforms: readonly string[];
}

export interface IAlineaTrueDataSelection extends IAlineaTrueDataMetricMapping {
  /** 页面指标 value；只作为父趋势线的关联键，不参与本地化展示。 */
  pageMetric: string;
  /** 下载表头使用的已解析文案；图表仍以页面指标配置实时解析。 */
  legendName?: string;
}

export interface IAlineaTrueDataPoint {
  date: string;
  value: number;
}

export interface IAlineaTrueDataSeries {
  selection: IAlineaTrueDataSelection;
  points: IAlineaTrueDataPoint[];
}

interface IAlineaTrueDataEligibilityParams {
  gameId?: string;
  entityType?: string;
  dateType?: string;
  selections?: IAlineaTrueDataSelection[];
}

interface IAlineaTrueDataAggregateParams {
  params?: {
    markets?: unknown;
    regions?: unknown;
    b_group_by_market?: boolean;
    b_group_by_region?: boolean;
    b_group_by_platform?: boolean;
    b_group_by_segment?: boolean;
  };
  byDevice?: boolean;
  byCountry?: boolean;
}

export interface IAlineaHistoryResponse {
  code?: number;
  data?: {
    history?: IAlineaHistoryRecord[];
    count?: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/**
 * History 返回的是短 metric 名；图表和下载统一使用真数弹窗已有的标准 metric 名。
 */
const ALINEA_TRUE_DATA_METRIC_MAPPINGS = [
  {
    trueDataMetric: 'alinea_calibration__revenue',
    allowedPlatforms: ['steam'],
  },
  {
    trueDataMetric: 'alinea_calibration__copies_sold',
    allowedPlatforms: ['steam'],
  },
  {
    trueDataMetric: 'alinea_calibration__register_players',
    allowedPlatforms: ['steam'],
  },
  {
    trueDataMetric: 'alinea_calibration__wishlist',
    allowedPlatforms: ['steam'],
  },
] as const;

export type TAlineaTrueDataMetric =
  (typeof ALINEA_TRUE_DATA_METRIC_MAPPINGS)[number]['trueDataMetric'];

const TRUE_DATA_METRIC_MAPPING_BY_STANDARD_NAME =
  ALINEA_TRUE_DATA_METRIC_MAPPINGS.reduce<Record<string, IAlineaTrueDataMetricMapping>>(
    (mapping, item) => ({ ...mapping, [item.trueDataMetric]: item }),
    {},
  );

/**
 * 判断页面或下载是否需要请求 Alinea History。
 * true 表示当前是 combine 日粒度、游戏明确且至少选中一个已映射指标。
 */
export const shouldRequestAlineaTrueData = ({
  gameId,
  entityType,
  dateType,
  selections = [],
}: IAlineaTrueDataEligibilityParams): boolean =>
  Boolean(
    gameId &&
      entityType?.toLowerCase() === 'combine' &&
      dateType?.toLowerCase() === 'daily' &&
      selections.length,
  );

/**
 * 判断当前趋势口径是否为 Global 聚合。
 * true 表示没有任何国家、地区、平台或 Segment 拆分，且市场仅选择 Global。
 */
export const isAlineaTrueDataGlobalAggregate = ({
  params = {},
  byDevice = false,
  byCountry = false,
}: IAlineaTrueDataAggregateParams): boolean => {
  let markets: unknown[] = [];
  if (Array.isArray(params?.markets) && params.markets.length) {
    markets = params.markets;
  } else if (Array.isArray(params?.regions)) {
    markets = params.regions;
  }

  return Boolean(
    !byDevice &&
      !byCountry &&
      !params?.b_group_by_market &&
      !params?.b_group_by_region &&
      !params?.b_group_by_platform &&
      !params?.b_group_by_segment &&
      markets.length === 1 &&
      String(markets[0]).toLowerCase() === 'global',
  );
};

const normalizeString = (value: unknown): string =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const normalizePlatforms = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(normalizeString).filter(Boolean);
  if (!value) return [];
  return [normalizeString(value)].filter(Boolean);
};

/**
 * Alinea History 的短指标名和平台字段在多个入口都会被消费；统一在响应边界转成页面内部契约，
 * 避免趋势图、真数表格和下载分别维护 metric / metric_name 的兼容逻辑。
 */
export const normalizeAlineaTrueDataRows = (
  histories: Array<Record<string, unknown>> = [],
): IAlineaHistoryRecord[] => {
  return histories.map((history) => {
    const rawMetric = history?.metric || history?.metric_name || '';
    const metric = normalizeString(rawMetric);
    let metricName = '';
    if (metric) {
      metricName = metric.startsWith('alinea_calibration__')
        ? metric
        : `alinea_calibration__${metric}`;
    }
    const platform = normalizePlatforms(history?.platform);

    return {
      ...history,
      metric: metricName,
      platform,
      alinea_calibration_region: history?.region,
      release_time: history?.release_date,
    };
  });
};

const toFiniteNumber = (
  value: string | number | null | undefined,
): number | null => {
  if (
    value === null ||
    value === undefined ||
    (typeof value === 'string' && !value.trim())
  )
    return null;

  const numberValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numberValue) ? numberValue : null;
};

/**
 * 判断 History 记录是否满足当前趋势图的展示边界。
 * - region 必须为 global，避免将国家/地区记录叠加到 Global 趋势。
 * - platform 由指标映射声明，不能使用页面请求中的 platforms 代替。
 * - 每条记录只能归属一个平台，避免 total xbox 等聚合记录误映射到 Steam 指标。
 */
const isGlobalAndMappedPlatformRow = (
  row: IAlineaHistoryRecord,
  mapping: IAlineaTrueDataMetricMapping,
): boolean => {
  const region = normalizeString(row?.region);
  const platforms = normalizePlatforms(row?.platform);

  // Global + 指标允许的平台必须同时满足；页面 platforms 参数只描述趋势筛选，不参与这里的判断。
  return (
    region === 'global' &&
    platforms.length === 1 &&
    mapping.allowedPlatforms.includes(platforms[0])
  );
};

const getMappingForRow = (
  row: IAlineaHistoryRecord,
): IAlineaTrueDataMetricMapping | undefined => {
  const rawMetric = normalizeString(row?.metric || row?.metric_name);
  if (!rawMetric) return undefined;

  const standardMetric = rawMetric.startsWith('alinea_calibration__')
    ? rawMetric
    : `alinea_calibration__${rawMetric}`;
  return TRUE_DATA_METRIC_MAPPING_BY_STANDARD_NAME[standardMetric];
};

/**
 * 仅消费 code=0 的 History 响应；count 是统计字段，不能代替 history 数组作为数据源。
 * 返回 null 用于区分“接口失败”和“接口成功但没有数据”，由调用层决定是否记录错误。
 */
export const getAlineaHistoryRecords = (
  response: IAlineaHistoryResponse | null | undefined,
): IAlineaHistoryRecord[] | null => {
  if (response?.code !== 0) return null;
  if (!Array.isArray(response?.data?.history)) return null;
  return normalizeAlineaTrueDataRows(response.data.history);
};

/**
 * 将当前已选页面指标绑定到标准真数 metric，未配置或不属于四项目标指标的选项自动排除。
 */
export const getAlineaTrueDataSelections = (
  selectedMetrics: Array<{
    value?: string;
    trueDataMetric?: TAlineaTrueDataMetric;
    legendName?: string;
  }>,
): IAlineaTrueDataSelection[] => {
  const seen = new Set<string>();

  return selectedMetrics.reduce<IAlineaTrueDataSelection[]>(
    (selections, metric) => {
      const trueDataMetric = metric?.trueDataMetric;
      if (!metric?.value || !trueDataMetric || seen.has(trueDataMetric))
        return selections;

      const mapping = TRUE_DATA_METRIC_MAPPING_BY_STANDARD_NAME[trueDataMetric];
      if (!mapping) return selections;

      seen.add(trueDataMetric);
      selections.push({
        ...mapping,
        pageMetric: metric.value,
        ...(metric.legendName ? { legendName: metric.legendName } : {}),
      });
      return selections;
    },
    [],
  );
};

/**
 * 过滤并整理 History 点位，供趋势图和下载共同使用，避免两个入口出现不同的 Global/Steam 口径。
 * 日粒度由调用层控制；这里专注于记录级日期闭区间、指标、平台、数值和去重规则。
 */
export const filterAlineaTrueData = (params: {
  rows?: IAlineaHistoryRecord[];
  selections?: IAlineaTrueDataSelection[];
  startDate?: string;
  endDate?: string;
}): IAlineaTrueDataSeries[] => {
  const {
    rows = [],
    selections = [],
    startDate = '',
    endDate = '',
  } = params;
  if (!rows.length || !selections.length) return [];

  const selectedMetrics = new Set(
    selections.map((selection) => selection.trueDataMetric),
  );
  const pointsByMetric: Record<string, IAlineaTrueDataPoint[]> = {};
  const seenPointKeys = new Set<string>();

  rows.forEach(row => {
    const mapping = getMappingForRow(row);
    if (!mapping || !selectedMetrics.has(mapping.trueDataMetric)) return;
    if (!isGlobalAndMappedPlatformRow(row, mapping)) return;

    const date = normalizeString(row?.date || row?.latest_time);
    if (!date || (startDate && date < startDate) || (endDate && date > endDate))
      return;

    const value = toFiniteNumber(row?.metric_value ?? row?.value);
    if (value === null) return;

    const pointKey = `${mapping.trueDataMetric}:${date}`;
    if (seenPointKeys.has(pointKey)) return;

    seenPointKeys.add(pointKey);
    if (!pointsByMetric[mapping.trueDataMetric])
      pointsByMetric[mapping.trueDataMetric] = [];
    pointsByMetric[mapping.trueDataMetric].push({
      date,
      value,
    });
  });

  return selections.reduce<IAlineaTrueDataSeries[]>(
    (result, selection) => {
      const points = pointsByMetric[selection.trueDataMetric];
      if (!points?.length) return result;

      result.push({
        selection,
        points: points.sort((a, b) => a.date.localeCompare(b.date)),
      });
      return result;
    },
    [],
  );
};
